import type { CustomTextTemplateBox, TextTypeEnum, VignetteTextTypeEnum } from '@lite-workspace/dto-fotoclass-templates';
import { BoxType } from '@lite-workspace/dto-fotoclass-templates';
import type { DrawableTextData } from '../../interfaces/drawable-data.interface';
import {
  buildLegacyTextMetrics,
  buildTextPassthroughMeta,
  carriedBoxFields,
  isTextPassthroughMetaStale,
  mergeBoxPassthroughMeta,
  mergeLegacyTextMetrics,
  mergeTextPassthroughMeta,
} from '../passthrough';
import { buildDrawableEffects, mergeDrawableEffects } from './effects';
import { finiteOrZero, rectFromBox, rectToBox } from './rect';
import { mergeRotation } from './rotation';
import type { BoxDecoder, BoxEncoder } from './codec-contract';

export const decode: BoxDecoder<CustomTextTemplateBox, DrawableTextData> = (box, ctx) => {
  const { params } = box;
  const fontSize = params.fontSize;
  const legacyMetrics = params.legacyTextMetrics;

  return {
    id: box.id,
    type: 'text',
    ...carriedBoxFields(box),
    order: 0, // dispatcher проставляет реальный order по индексу в collage
    canResize: false,
    opacity: box.opacity,
    blendMode: box.blendMode,
    visible: box.visible,
    locked: box.locked,
    rotation: box.rotation,
    effects: buildDrawableEffects(box.effects),
    text: {
      text: params.text ?? '',
      color: params.color,
      // tracking is em/1000 (PS convention), letterSpacing is absolute px — convert через fontSize.
      // Mirrors buildLegacyTextMetrics' own condition: the bag only holds these two when the division by
      // fontSize was not invertible, so a live positive fontSize must never lose to a stale bag.
      letterSpacing: fontSize > 0 ? ((params.tracking ?? 0) / 1000) * fontSize : (legacyMetrics?.letterSpacing ?? 0),
      lineHeight: fontSize > 0 ? params.lineHeightPx / fontSize : (legacyMetrics?.lineHeight ?? 1),
      fontName: params.fontName,
      fontWeight: params.fontWeight,
      postScriptName: params.postScriptName,
      italic: params.italic,
      underline: params.underline,
      strikethrough: params.strikethrough,
      fillFlag: params.fillFlag,
      fillGradient: params.fillGradient,
      fillGradientDisabled: params.fillGradientDisabled,
      fauxBold: params.fauxBold,
      align: params.align,
      fontCaps: params.fontCaps,
      fontBaseline: params.fontBaseline,
      horizontalScale: params.horizontalScale,
      verticalScale: params.verticalScale,
      orientation: params.orientation,
      transform: params.transform,
      anchor: params.anchor,
      textPath: params.textPath,
      areaShape: params.areaShape,
      ...(params.ignoreTextWrap && { ignoreTextWrap: true }),
      ...(params.textWrapGroups && params.textWrapGroups.length > 0 && { textWrapGroups: params.textWrapGroups }),
      warp: params.warp,
      ...mergeLegacyTextMetrics(legacyMetrics),
    },
    textType: params.textType,
    fioLayout: params.fioLayout,
    textMode: params.textMode,
    autoWidth: params.autoWidth,
    autoFit: params.autoFit,
    maxFontSize: params.maxFontSize,
    verticalAlign: params.verticalAlign,
    editedByClient: params.editedByClient,
    // Ключ только при значении: безусловная запись материализует `memoryEntry: undefined` там, где твин ключа не заводит.
    ...(params.memoryEntry !== undefined ? { memoryEntry: params.memoryEntry } : {}),
    ...(params.leadLine === true && { leadLine: true }),
    ...(params.vignetteQuote === true && { vignetteQuote: true }),
    ...(params.quoteNominalCapacity !== undefined && { quoteNominalCapacity: params.quoteNominalCapacity }),
    ...(params.quoteFloorCapacity !== undefined && { quoteFloorCapacity: params.quoteFloorCapacity }),
    ...(params.quoteFloorFontSize !== undefined && { quoteFloorFontSize: params.quoteFloorFontSize }),
    // Only the text half has a slot: `templateParams.naturalAligns` has no deciding reader (verify.ts narrowing).
    templateParams: params.templateText !== undefined ? { text: params.templateText } : undefined,
    naturalAligns: { container: rectFromBox(box.rect, ctx), fontSize },
    templateMeta: buildTextPassthroughMeta(box, params),
  };
};

export const encode: BoxEncoder<DrawableTextData, CustomTextTemplateBox> = (drawable, ctx) => {
  const t = drawable.text;
  const fontSize = finiteOrZero(drawable.naturalAligns.fontSize, ctx, 'naturalAligns.fontSize');
  // Stale bag (fontName drifted since build) must be dropped whole — box-level fields included, not just typography.
  const boxLevelMeta = isTextPassthroughMetaStale(t.fontName, drawable.templateMeta) ? undefined : drawable.templateMeta;

  // `text.lineHeight`/`text.letterSpacing` get the same finite guard as rect and fontSize: they are
  // multiplied/divided into `lineHeightPx`/`tracking`, so a missing or null value produced a NaN that
  // JSON.stringify wrote as `null` — silently, and unreadable by the next decode.
  const lineHeight = finiteOrZero(t.lineHeight, ctx, 'text.lineHeight');
  const letterSpacing = finiteOrZero(t.letterSpacing, ctx, 'text.letterSpacing');

  const box: CustomTextTemplateBox = {
    type: BoxType.TEXT,
    id: drawable.id,
    ...carriedBoxFields(drawable),
    rect: rectToBox(drawable.naturalAligns.container, ctx),
    rotation: mergeRotation(drawable, ctx),
    opacity: drawable.opacity,
    blendMode: drawable.blendMode,
    visible: drawable.visible,
    locked: drawable.locked,
    // boxLevelMeta (not templateMeta directly) — a stale bag drops its rawEffects together with psdLayerId etc.
    effects: mergeDrawableEffects(
      drawable.effects,
      boxLevelMeta?.rawEffects,
      drawable.layerStyleRendering === 'live' ? 'typed' : undefined,
    ),
    params: {
      fontName: t.fontName,
      fontWeight: t.fontWeight,
      fontSize,
      lineHeightPx: lineHeight * fontSize,
      align: t.align ?? 'left',
      color: t.color,
      italic: t.italic,
      text: t.text,
      tracking: fontSize > 0 ? (letterSpacing / fontSize) * 1000 : 0,
      postScriptName: t.postScriptName,
      underline: t.underline,
      strikethrough: t.strikethrough,
      fillFlag: t.fillFlag,
      fillGradient: t.fillGradient,
      fillGradientDisabled: t.fillGradientDisabled,
      fauxBold: t.fauxBold,
      fontCaps: t.fontCaps,
      fontBaseline: t.fontBaseline,
      horizontalScale: t.horizontalScale,
      verticalScale: t.verticalScale,
      orientation: t.orientation,
      transform: t.transform,
      anchor: t.anchor,
      textPath: t.textPath,
      areaShape: t.areaShape,
      ...(t.ignoreTextWrap && { ignoreTextWrap: true }),
      ...(t.textWrapGroups && t.textWrapGroups.length > 0 && { textWrapGroups: [...t.textWrapGroups] }),
      warp: t.warp,
      textType: drawable.textType as TextTypeEnum | VignetteTextTypeEnum | undefined,
      fioLayout: drawable.fioLayout,
      textMode: drawable.textMode,
      autoWidth: drawable.autoWidth,
      autoFit: drawable.autoFit,
      maxFontSize: drawable.maxFontSize,
      verticalAlign: drawable.verticalAlign,
      legacyTextMetrics: buildLegacyTextMetrics(t, fontSize),
      editedByClient: drawable.editedByClient,
      ...(drawable.memoryEntry !== undefined ? { memoryEntry: drawable.memoryEntry } : {}),
      ...(drawable.leadLine === true && { leadLine: true }),
      ...(drawable.vignetteQuote === true && { vignetteQuote: true }),
      ...(drawable.quoteNominalCapacity !== undefined && { quoteNominalCapacity: drawable.quoteNominalCapacity }),
      ...(drawable.quoteFloorCapacity !== undefined && { quoteFloorCapacity: drawable.quoteFloorCapacity }),
      ...(drawable.quoteFloorFontSize !== undefined && { quoteFloorFontSize: drawable.quoteFloorFontSize }),
      templateText: drawable.templateParams?.text,
      ...mergeTextPassthroughMeta(t.fontName, drawable.templateMeta),
    },
  };

  return mergeBoxPassthroughMeta(box, boxLevelMeta);
};
